"use client";

// MobileBottomBar — floating dock navigation on mobile (<md).
// Slots: Home · Categories · Search · Wishlist · Cart · Account (Wishlist only
// when the store has it on in Settings → Products). Search lives here on
// phones, like account/wishlist/cart; the header keeps it on desktop.
// Stores with no categories get Products in the second slot instead, so the
// tab never lands on an empty /categories page.
// Hidden on tablets/desktop where the header has space for everything.
//
// A dock, not a bar: inset from the edges, fully rounded, lifted on a shadow
// over a blurred surface, so page content runs underneath it rather than
// stopping at a full-width slab.
//
// Icons carry the navigation on their own. With the labels gone the active
// state has to do that work, so it is a solid brand disc behind the icon plus
// the icon's own filled weight. A 10% tint was the first attempt and read as
// grey rather than branded at this size — with no labels the active signal has
// to be unmistakable, and the disc doubles as the one place each tenant's brand
// colour shows up in the chrome. Every item keeps its label as screen-reader text:
// dropping a visible label must not drop the accessible name.
//
// Always on screen. While any scroll is in progress the pill eases a little
// smaller, and it settles back to full size once scrolling stops (Instagram on
// iOS). It is a transform only, so nothing on the page reflows. (It used to
// hide on scroll down; stores asked for it to stay put.)
import { c as _c } from "react/compiler-runtime";
import { Heart, House, MagnifyingGlass, ShoppingCart, SquaresFour, Storefront, UserCircle } from "@phosphor-icons/react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";
import { usePageIdle } from "@/hooks/use-page-idle";
import { useSessionUser } from "@/lib/auth/use-session-user";
import { useCart, useCartDrawer } from "@/lib/cart/CartContext";
import { useWishlistEnabled } from "@/lib/storefront/wishlist-enabled";
import { useWishlist } from "@/lib/wishlist/WishlistContext";
type Props = {
  /**
   * Whether the second slot is Categories. Resolved server-side in the
   * storefront layout from the store's bottom-bar setting, and false when the
   * store has no active top-level categories — false swaps it for Products.
   */
  showCategories: boolean;
};
const StorefrontSearchOverlay = dynamic(() => import("@/components/storefront/StorefrontSearchOverlay"), {
  ssr: false
});
const ICON_SIZE = 23;
/** Quiet time after the last scroll before the dock returns to full size. */
const SETTLE_MS = 180;
export default function MobileBottomBar(t0) {
  const $ = _c(38);
  const {
    showCategories
  } = t0;
  const locale = useLocale();
  const pathname = usePathname();
  const isRTL = locale === "ar";
  const {
    user
  } = useSessionUser();
  const {
    lineCount
  } = useCart();
  const {
    openDrawer
  } = useCartDrawer();
  const {
    count: wishlistCount
  } = useWishlist();
  const wishlistEnabled = useWishlistEnabled();
  const [compact, setCompact] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const pageIdle = usePageIdle();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      let queued = false;
      let settle;
      const update = () => {
        queued = false;
        setCompact(true);
        clearTimeout(settle);
        settle = setTimeout(() => setCompact(false), SETTLE_MS);
      };
      const onScroll = () => {
        if (queued) {
          return;
        }
        queued = true;
        requestAnimationFrame(update);
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", onScroll);
        clearTimeout(settle);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const path = pathname.replace(new RegExp(`^/${locale}`), "") || "/";
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setCompact(false);
      setSearchOpen(false);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== pathname) {
    t4 = [pathname];
    $[3] = pathname;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(t3, t4);
  const accountHref = user ? `/${locale}/account/profile` : `/${locale}/login`;
  const wishlistHref = `/${locale}/wishlist`;
  const t5 = isRTL ? "\u0627\u0644\u062A\u0646\u0642\u0644 \u0627\u0644\u0633\u0641\u0644\u064A" : "Bottom navigation";
  const t6 = "md:hidden pointer-events-none fixed inset-x-0 bottom-0 z-40 px-4 pb-[calc(var(--bottom-bar-gap)+var(--bottom-bar-safe))]";
  const t7 = `pointer-events-auto mx-auto flex origin-bottom transition-transform duration-300 ease-out motion-reduce:scale-100 motion-reduce:transition-none ${compact ? "scale-[0.88]" : "scale-100"} h-[var(--bottom-bar-dock)] max-w-sm items-center justify-around rounded-full border border-border/70 bg-background/90 px-1.5 shadow-[0_6px_24px_-4px_rgb(0_0_0/0.18),0_2px_6px_-2px_rgb(0_0_0/0.10)] backdrop-blur-xl supports-[backdrop-filter]:bg-background/75`;
  const t8 = isRTL ? "\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629" : "Home";
  const t9 = path === "/";
  let t10;
  if ($[5] !== locale || $[6] !== t8 || $[7] !== t9) {
    t10 = <BarLink href="/" icon={House} label={t8} active={t9} locale={locale} />;
    $[5] = locale;
    $[6] = t8;
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const t11 = showCategories ? <BarLink href="/categories" icon={SquaresFour} label={isRTL ? "\u0627\u0644\u0623\u0642\u0633\u0627\u0645" : "Categories"} active={path.startsWith("/categories")} locale={locale} /> : <BarLink href="/products" icon={Storefront} label={isRTL ? "\u0627\u0644\u0645\u0646\u062A\u062C\u0627\u062A" : "Products"} active={path.startsWith("/products")} locale={locale} />;
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setSearchOpen(true);
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const t13 = isRTL ? "\u0628\u062D\u062B" : "Search";
  let t14;
  if ($[10] !== t13) {
    t14 = <BarButton onClick={t12} icon={MagnifyingGlass} label={t13} />;
    $[10] = t13;
    $[11] = t14;
  } else {
    t14 = $[11];
  }
  const t15 = wishlistEnabled && <BarLink href={wishlistHref} icon={Heart} label={isRTL ? "\u0627\u0644\u0645\u0641\u0636\u0644\u0629" : "Wishlist"} active={path.startsWith("/wishlist")} locale={locale} rawHref={true} badge={wishlistCount > 0 ? wishlistCount : undefined} />;
  const t16 = isRTL ? "\u0627\u0644\u0633\u0644\u0629" : "Cart";
  const t17 = lineCount > 0 ? lineCount : undefined;
  let t18;
  if ($[12] !== openDrawer || $[13] !== t16 || $[14] !== t17) {
    t18 = <BarButton onClick={openDrawer} icon={ShoppingCart} label={t16} badge={t17} />;
    $[12] = openDrawer;
    $[13] = t16;
    $[14] = t17;
    $[15] = t18;
  } else {
    t18 = $[15];
  }
  const T0 = BarLink;
  const t19 = UserCircle;
  const t20 = isRTL ? "\u062D\u0633\u0627\u0628\u064A" : "Account";
  const t21 = path.startsWith("/account");
  let t22;
  if ($[16] !== T0 || $[17] !== accountHref || $[18] !== locale || $[19] !== t19 || $[20] !== t20 || $[21] !== t21) {
    t22 = <T0 href={accountHref} icon={t19} label={t20} active={t21} locale={locale} rawHref={true} />;
    $[16] = T0;
    $[17] = accountHref;
    $[18] = locale;
    $[19] = t19;
    $[20] = t20;
    $[21] = t21;
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t14 || $[26] !== t15 || $[27] !== t18 || $[28] !== t22 || $[29] !== t7) {
    t23 = <ul className={t7}>{t10}{t11}{t14}{t15}{t18}{t22}</ul>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t14;
    $[26] = t15;
    $[27] = t18;
    $[28] = t22;
    $[29] = t7;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== pageIdle || $[32] !== searchOpen) {
    t24 = (pageIdle || searchOpen) && <StorefrontSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />;
    $[31] = pageIdle;
    $[32] = searchOpen;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== t23 || $[35] !== t24 || $[36] !== t5) {
    t25 = <nav aria-label={t5} className={t6}>{t23}{t24}</nav>;
    $[34] = t23;
    $[35] = t24;
    $[36] = t5;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  return t25;
}
type IconComponent = typeof House;

/**
 * The tappable disc every slot shares.
 *
 * 44px so the target still meets the touch minimum now that the label is gone
 * and no longer part of the hit area.
 */
function Slot(t0) {
  const $ = _c(12);
  const {
    icon: Icon,
    label,
    active,
    badge
  } = t0;
  const t1 = `relative flex h-11 w-11 items-center justify-center rounded-full transition-colors ${active ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"}`;
  const t2 = active ? "fill" : "regular";
  let t3;
  if ($[0] !== Icon || $[1] !== t2) {
    t3 = <Icon size={ICON_SIZE} weight={t2} />;
    $[0] = Icon;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== badge) {
    t4 = badge !== undefined && <span className="absolute -end-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-primary-foreground">{badge > 99 ? "99+" : badge}</span>;
    $[3] = badge;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== label) {
    t5 = <span className="sr-only">{label}</span>;
    $[5] = label;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <span className={t1}>{t3}{t4}{t5}</span>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function BarLink(t0) {
  const $ = _c(9);
  const {
    href,
    icon,
    label,
    active,
    locale,
    rawHref,
    badge
  } = t0;
  const finalHref = rawHref ? href : `/${locale}${href === "/" ? "" : href}`;
  const t1 = active ? "page" : undefined;
  let t2;
  if ($[0] !== active || $[1] !== badge || $[2] !== icon || $[3] !== label) {
    t2 = <Slot icon={icon} label={label} active={active} badge={badge} />;
    $[0] = active;
    $[1] = badge;
    $[2] = icon;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== finalHref || $[6] !== t1 || $[7] !== t2) {
    t3 = <li><Link href={finalHref} aria-current={t1} className="flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">{t2}</Link></li>;
    $[5] = finalHref;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function BarButton(t0) {
  const $ = _c(8);
  const {
    onClick,
    icon,
    label,
    badge
  } = t0;
  let t1;
  if ($[0] !== badge || $[1] !== icon || $[2] !== label) {
    t1 = <Slot icon={icon} label={label} active={false} badge={badge} />;
    $[0] = badge;
    $[1] = icon;
    $[2] = label;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== label || $[5] !== onClick || $[6] !== t1) {
    t2 = <li><button type="button" onClick={onClick} aria-label={label} className="flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">{t1}</button></li>;
    $[4] = label;
    $[5] = onClick;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}